/**
 * Sample Song Card Component
 *
 * Matches original HTML structure: .song-card with sample data for unauthenticated users.
 */

import type { SampleSong } from '@/types';
import { play, currentTrackId, isPlaying } from '@/stores/player';
import { openLoginPrompt } from '@/stores/ui';

interface SampleSongCardProps {
  song: SampleSong;
}

export function SampleSongCard({ song }: SampleSongCardProps) {
  const isCurrentlyPlaying = currentTrackId.value === song.spotify_id && isPlaying.value;

  const handlePlay = () => {
    play(song);
  };

  const cardClasses = ['song-card'];
  if (isCurrentlyPlaying) cardClasses.push('playing');

  return (
    <div
      class={cardClasses.join(' ')}
      data-spotify-id={song.spotify_id}
      data-is-sample-data="true"
      data-spotify-track-group="sampleSongs"
    >
      {/* Mobile-only floating likes */}
      <div class="likes-pill mobile-floating-only">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"></path>
          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
        </svg>
        <span class="likes-count">{song.votes.score}</span>
      </div>

      {/* Song image - no play overlay, matches original */}
      <div class="song-image">
        <img
          src={song.image || '/images/logo-transparent_buffer.png'}
          alt={`${song.title} by ${song.artist}`}
          loading="eager"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/images/logo-transparent_buffer.png';
          }}
        />
      </div>

      {/* Song info */}
      <div class="song-info">
        <div class="song-title-wrapper">
          <div class="song-title-play-btn" onClick={handlePlay}>
            <div class="play-icon"></div>
            <div class="pause-icon"></div>
          </div>
          <a
            href={`https://open.spotify.com/track/${song.spotify_id}`}
            target="_blank"
            rel="noopener noreferrer"
            class="song-title spotify-content-link"
          >
            {song.title}
          </a>
          <div class="playlist-badge">
            <svg viewBox="0 0 135 135" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.625 67.5C5.625 33.3281 33.3281 5.625 67.5 5.625C101.672 5.625 129.375 33.3281 129.375 67.5C129.375 101.672 101.672 129.375 67.5 129.375C33.3281 129.375 5.625 101.672 5.625 67.5ZM97.8638 54.1125C98.8631 53.0468 99.4086 51.6342 99.3848 50.1735C99.3611 48.7128 98.77 47.3186 97.7367 46.2859C96.7033 45.2533 95.3087 44.6632 93.8479 44.6405C92.3872 44.6178 90.975 45.1643 89.91 46.1644L56.0981 79.9706L45.4444 69.3225C44.3835 68.2979 42.9626 67.7309 41.4877 67.7437C40.0129 67.7565 38.6021 68.3481 37.5591 69.391C36.5162 70.4339 35.9247 71.8448 35.9118 73.3196C35.899 74.7945 36.466 76.2154 37.4906 77.2763L56.0981 95.8838L97.8638 54.1181V54.1125Z" fill="#1DB954"/>
            </svg>
          </div>
        </div>

        <p class="song-artist">{song.artist}</p>
        {song.album && <p class="song-album">{song.album}</p>}

        <div class="song-meta-stacked">
          <div class="submitter-info">
            Submitted by {song.submitter}
          </div>
          <div class="likes-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"></path>
              <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
            </svg>
            <span class="likes-count">{song.votes.score}</span>
          </div>
        </div>

        {/* Vote controls - click shows login prompt for sample songs */}
        <div class="vote-controls">
          <button class="vote-btn vote-like" data-song-id={song.id} data-vote="like" onClick={() => openLoginPrompt()}>Like</button>
          <button class="vote-btn vote-neutral" data-song-id={song.id} data-vote="neutral" onClick={() => openLoginPrompt()}>Neutral</button>
          <button class="vote-btn vote-dislike" data-song-id={song.id} data-vote="dislike" onClick={() => openLoginPrompt()}>Dislike</button>
        </div>
      </div>
    </div>
  );
}
